export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  token?: string;
};

type ApiRequestOptions = Omit<RequestOptions, 'body' | 'method'>;

const API_BASE_URL = (process.env.REACT_APP_API_URL ?? '').replace(/\/+$/, '');
const AUTH_STORAGE_KEY = 'responseops.auth';
const AUTH_REFRESHED_EVENT = 'responseops.auth-refreshed';
const AUTH_EXPIRED_EVENT = 'responseops.auth-expired';
const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

type StoredSession = {
  accessToken?: string;
  refreshToken?: string;
  [key: string]: unknown;
};

let refreshPromise: Promise<string> | null = null;

export async function request<TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> {
  const { body, headers, token, ...init } = options;
  const requestHeaders = new Headers(headers);
  const hasBody = body !== undefined;

  if (hasBody && !(body instanceof FormData)) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  const useStoredAuth = !token && !isPublicAuthPath(path);
  const authToken = token ?? (useStoredAuth ? await getValidAccessToken() : undefined);

  if (authToken) {
    requestHeaders.set('Authorization', `Bearer ${authToken}`);
  }

  const requestUrl = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const requestInit: RequestInit = {
    ...init,
    body: hasBody ? serializeBody(body) : undefined,
    headers: requestHeaders,
  };

  let response = await fetch(requestUrl, requestInit);

  if (response.status === 401 && useStoredAuth) {
    const refreshedToken = await refreshAccessToken();
    requestHeaders.set('Authorization', `Bearer ${refreshedToken}`);
    response = await fetch(requestUrl, { ...requestInit, headers: requestHeaders });
  }

  const payload = await parseResponse(response);

  if (!response.ok) {
    const message = getErrorMessage(payload) ?? `Request failed with status ${response.status}`;

    if (useStoredAuth && shouldExpireSession(response.status, message, payload)) {
      expireStoredSession();
    }

    throw new ApiError(message, response.status, payload);
  }

  return payload as TResponse;
}

export const api = {
  get<TResponse>(path: string, options?: ApiRequestOptions) {
    return request<TResponse>(path, { ...options, method: 'GET' });
  },

  post<TResponse>(path: string, body?: unknown, options?: ApiRequestOptions) {
    return request<TResponse>(path, { ...options, body, method: 'POST' });
  },

  put<TResponse>(path: string, body?: unknown, options?: ApiRequestOptions) {
    return request<TResponse>(path, { ...options, body, method: 'PUT' });
  },

  patch<TResponse>(path: string, body?: unknown, options?: ApiRequestOptions) {
    return request<TResponse>(path, { ...options, body, method: 'PATCH' });
  },

  delete<TResponse>(path: string, options?: ApiRequestOptions) {
    return request<TResponse>(path, { ...options, method: 'DELETE' });
  },
};

function serializeBody(body: unknown): BodyInit {
  if (
    body instanceof Blob
    || body instanceof FormData
    || body instanceof URLSearchParams
    || typeof body === 'string'
  ) {
    return body;
  }

  return JSON.stringify(body);
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

function getErrorMessage(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object') {
    const source = payload as Record<string, unknown>;

    for (const key of ['message', 'detail', 'error']) {
      const value = source[key];

      if (typeof value === 'string' && value) {
        return value;
      }
    }
  }

  return typeof payload === 'string' && payload ? payload : undefined;
}

function getStoredAccessToken() {
  return getStoredSession()?.accessToken;
}

async function getValidAccessToken() {
  const accessToken = getStoredAccessToken();

  if (!accessToken || !isTokenExpiring(accessToken)) {
    return accessToken;
  }

  return refreshAccessToken();
}

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

async function performTokenRefresh() {
  const session = getStoredSession();

  if (!session?.refreshToken) {
    expireStoredSession();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    });
    const payload = await parseResponse(response);

    if (!response.ok || !payload || typeof payload !== 'object') {
      throw new ApiError(getErrorMessage(payload) ?? 'Unable to refresh session', response.status, payload);
    }

    const source = payload as Record<string, unknown>;
    const accessToken = readString(source, 'access_token')
      ?? readString(source, 'accessToken')
      ?? readString(source, 'token');
    const refreshToken = readString(source, 'refresh_token')
      ?? readString(source, 'refreshToken')
      ?? session.refreshToken;

    if (!accessToken) {
      throw new ApiError('Refresh response is missing an access token', 401, payload);
    }

    const updatedSession = { ...session, accessToken, refreshToken };
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updatedSession));
    window.dispatchEvent(new CustomEvent(AUTH_REFRESHED_EVENT, { detail: { accessToken, refreshToken } }));
    return accessToken;
  } catch (error) {
    expireStoredSession();
    throw error instanceof ApiError
      ? error
      : new ApiError('Your session has expired. Please sign in again.', 401, error);
  }
}

function getStoredSession(): StoredSession | undefined {
  try {
    const stored = window.localStorage.getItem(AUTH_STORAGE_KEY);

    if (!stored) {
      return undefined;
    }

    const session = JSON.parse(stored) as StoredSession;
    return session && typeof session === 'object' ? session : undefined;
  } catch {
    return undefined;
  }
}

function isTokenExpiring(token: string) {
  try {
    const [, payload] = token.split('.');
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(window.atob(normalizedPayload)) as { exp?: unknown };
    return typeof decoded.exp === 'number'
      && decoded.exp <= Math.floor(Date.now() / 1000) + TOKEN_EXPIRY_BUFFER_SECONDS;
  } catch {
    return false;
  }
}

function expireStoredSession() {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

function shouldExpireSession(status: number, message: string, payload: unknown) {
  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const details = `${message} ${JSON.stringify(payload)}`.toLowerCase();
  return [
    'deactivated',
    'inactive',
    'disabled',
    'revoked',
    'suspended',
    'session expired',
    'session invalid',
  ].some((marker) => details.includes(marker));
}

function isPublicAuthPath(path: string) {
  return path === '/auth/login'
    || path === '/auth/signup'
    || path === '/auth/refresh'
    || path === '/auth/verify-email'
    || path === '/auth/verify-email/resend'
    || path === '/auth/password/forgot'
    || path === '/auth/password/reset'
    || path === '/auth/admin-invitations/complete';
}

function readString(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === 'string' && value ? value : undefined;
}
